import React, { useCallback, useMemo, useState, useContext, useEffect, useRef } from "react";
import moment from "moment";
import { BackLink, Intro, Loader } from "components/commons";
import { locale } from "localization/en";
import { FormMode, PaymentMethod, Path, ShiftStatus } from "enums";
import PanelButtons from "components/commons/panel-button/panel-buttons";
import { useApi, useForm, useMount, useRouter, useModal, useAuth } from "hooks";
import { getShiftByShiftId, updateShiftById, validateShiftByShiftId } from "apis/shift-records.api";
import ShiftRecordsDetailsInfoModule from "./shift-records-details-info/shift-records-details-info.module";
import ShiftRecordsDetailsCashAndNonCashModule from "./shift-records-details-sales-cash-non-cash/shift-records-details-cash-and-non-cash.module";
import ShiftRecordsDetailsStockInventoryWetModule from "./shift-records-details-stock-inventory-wet/shift-details-stock-inventory.module";
import ShiftRecordsDetailsStockInventoryDryModule from "./shift-records-details-stock-inventory-dry/shift-details-stock-inventory.module";
import { initialState as formState } from "./shift-records-details.state";
import ForceCloseShiftModal from "./shift-records-details-force-close-modal";
import Big from "big.js";
import { SuccessModal } from "components/modals";
import { StocqClockIn } from "images";
import { useHistory } from "react-router-dom";
import { StationContext } from "contexts";
import { find, sortBy } from "lodash";
import { parseNumber, formatNumber } from "utils";
import { calculateSyncProgress, initializePumpTotals } from "../shift-records.mapper";
import { sumBy } from "lodash";
import { removeComasStringAmount, stringToDecimal } from "utils/text.utils";
import { validateReadingDipstick } from "./shift-records-details-stock-inventory-wet/shift-details-stock-inventory-dipstick";
import ProductCode from "enums/product-code";
import { isAccountabilityPaymentMethod } from "data/constant/accountability";
import { prettifyProductCode } from "utils/pretty.utils";
import { getProductPumpsWithTransactions } from "./shift-records-details-stock-inventory-wet/shift-details-fuel-product-summary";
import { computeExpectedInventory } from "./shift-records-details-stock-inventory-dry/shift-details-stock-inventory.mapper";
import useProducts from "hooks/useProducts";
import {
  ShiftDetailsTab,
  computeCashVariance,
  countIncompleteFields,
  employeeShoulderKey,
  getIncompleteFieldsByTab,
} from "./shift-records-details-completion";
import ShiftRecordsDetailsIncompleteBanner from "./shift-records-details-incomplete-banner";
import ShiftRecordsDetailsIncompleteCounter from "./shift-records-details-incomplete-counter";

const SHOW_INCOMPLETE_FIELD_IDENTIFIER = true;

const AGGREGATED_CASH_FIELDS = [
  "quantity",
  "totalAmount",
  "discount",
  "vatAmount",
  "vatExempt",
  "finalAmount",
];

const deductFromAggregatedCash = (cash, movedPayments) =>
  movedPayments
    .reduce(
      (rows, payment) =>
        rows.map((row) =>
          !row.transactionPaymentMethodId &&
          row.sku === payment.sku &&
          (payment.type !== "fuel" || row.pumpNumber === payment.pumpNumber)
            ? AGGREGATED_CASH_FIELDS.reduce(
                (next, field) => ({
                  ...next,
                  [field]: Big(row[field] || 0)
                    .minus(payment[field] || 0)
                    .toNumber(),
                }),
                row,
              )
            : row,
        ),
      cash,
    )
    .filter((row) => row.transactionPaymentMethodId || Math.abs(row.totalAmount) >= 0.005);

const addToAggregatedCash = (cash, movedPayments) =>
  movedPayments.reduce((rows, payment) => {
    const isSameRow = (row) =>
      !row.transactionPaymentMethodId &&
      row.sku === payment.sku &&
      (payment.type !== "fuel" || row.pumpNumber === payment.pumpNumber);

    if (!rows.some(isSameRow)) {
      return [...rows, { ...payment, transactionPaymentMethodId: null }];
    }

    return rows.map((row) =>
      isSameRow(row)
        ? AGGREGATED_CASH_FIELDS.reduce(
            (next, field) => ({
              ...next,
              [field]: Big(row[field] || 0)
                .add(payment[field] || 0)
                .toNumber(),
            }),
            row,
          )
        : row,
    );
  }, cash);

const ShiftRecordsDetailsModule = () => {
  const { query, push: routerPush } = useRouter();
  const successModal = useModal();
  const prevShiftModal = useModal();
  const syncRequiredModal = useModal();
  const forceCloseModal = useModal();
  const history = useHistory();
  const shiftId = query?.id;
  const pageMode = query?.pageMode;
  const { station } = useContext(StationContext);
  const {
    fetch: fetchAllProducts,
    allProducts,
    loading: loadingProducts,
  } = useProducts({ withStationCode: false });
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();

  const [page, setPage] = useState(ShiftDetailsTab.Info);
  const [isHighlighted, setHighlighted] = useState(false);
  const highlightCursor = React.useRef(0);

  const [employees, setEmployees] = useState([]);
  const [employeesToPay, setEmployeesToPay] = useState([]);
  const [variance, setVariance] = useState(0);
  const [beginDidNotPerformDipStick, setBeginDidNotPerformDipStick] = useState(false);
  const [endDidNotPerformDipStick, setEndDidNotPerformDipStick] = useState(false);
  const [safeDropId, setSafeDropId] = useState(null);
  const [safeDrop, setSafeDrop] = useState([]);
  const [hasExistingShiftOpen, setExistingShiftOpen] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [remarksUgt, setRemarksUgt] = useState("");
  const [beginningBalance, setBeginningBalance] = useState(0);

  const [getShiftResult, setShiftResult] = useState(undefined);

  useMount(async () => {
    if (shiftId) {
      setIsLoading(true);
      if (allProducts.length <= 0) {
        fetchAllProducts();
      }
      const res = await getShiftRequest();
      setShiftResult(res);
      await fetchValidation();

      if (res.shift.stationCode !== station.stationCode) {
        return routerPush(Path.ShiftRecords);
      }
      const newFields = setFormData(res);

      let initShiftPumpTotals = [];
      if (!res.shift?.closedAt && res?.recentShift && res.shift?.status !== ShiftStatus.Closed) {
        getRecentShiftRequest({
          shiftId: res.recentShift.shiftId,
        }).then((recentShiftResponse) => {
          initShiftPumpTotals = initializePumpTotals({
            currentShift: res,
            recentShift: recentShiftResponse,
            pageMode,
          });

          formData?.modifyForm({
            shiftPumpTotals: { value: initShiftPumpTotals },
            ...newFields,
          });
          setIsLoading(false);
        });
      } else {
        initShiftPumpTotals = initializePumpTotals({
          currentShift: res,
          recentShift: null,
          pageMode,
        });
        formData.modifyForm({
          shiftPumpTotals: { value: initShiftPumpTotals },
          ...newFields,
        });
        setIsLoading(false);
      }

      let safeDropItem = {
        oneThousand: 0,
        fiveHundred: 0,
        twoHundred: 0,
        oneHundred: 0,
        fifty: 0,
        twenty: 0,
        twentyCoins: 0,
        ten: 0,
        five: 0,
        coinAmount: 0,
      };

      let safeDropEndShiftItem = {
        oneThousand: 0,
        fiveHundred: 0,
        twoHundred: 0,
        oneHundred: 0,
        fifty: 0,
        twenty: 0,
        twentyCoins: 0,
        ten: 0,
        five: 0,
        coinAmount: 0,
      };

      if (res) {
        if (res?.safeDrop) {
          safeDropItem = res.safeDrop;
        }

        if (res?.safeDropEndShift) {
          safeDropEndShiftItem = res.safeDropEndShift;
          setSafeDropId(safeDropEndShiftItem.safeDropId);
        }

        // setBeginDidNotPerformDipStick(!res.shift.isReadingDipstickBeginning);
        // setEndDidNotPerformDipStick(!res.shift.isReadingDipstickEnding);
      }

      let safeDropsList = [
        {
          key: "oneThousand",
          denomination: 1000,
          safeDropItem: safeDropItem.oneThousand,
          safeDrop: safeDropEndShiftItem.oneThousand || 0,
          total: Number(Big(safeDropItem.oneThousand).add(safeDropEndShiftItem.oneThousand)),
          totalAmount: Number(
            Big(safeDropItem.oneThousand).add(safeDropEndShiftItem.oneThousand).mul(1000),
          ),
        },
        {
          key: "fiveHundred",
          denomination: 500,
          safeDropItem: safeDropItem.fiveHundred,
          safeDrop: safeDropEndShiftItem.fiveHundred || 0,
          total: Number(Big(safeDropItem.fiveHundred).add(safeDropEndShiftItem.fiveHundred)),
          totalAmount: Number(
            Big(safeDropItem.fiveHundred).add(safeDropEndShiftItem.fiveHundred).mul(500),
          ),
        },
        {
          key: "twoHundred",
          denomination: 200,
          safeDropItem: safeDropItem.twoHundred,
          safeDrop: safeDropEndShiftItem.twoHundred || 0,
          total: Number(Big(safeDropItem.twoHundred).add(safeDropEndShiftItem.twoHundred)),
          totalAmount: Number(
            Big(safeDropItem.twoHundred).add(safeDropEndShiftItem.twoHundred).mul(200),
          ),
        },
        {
          key: "oneHundred",
          denomination: 100,
          safeDropItem: safeDropItem.oneHundred,
          safeDrop: safeDropEndShiftItem.oneHundred || 0,
          total: Number(Big(safeDropItem.oneHundred).add(safeDropEndShiftItem.oneHundred)),
          totalAmount: Number(
            Big(safeDropItem.oneHundred).add(safeDropEndShiftItem.oneHundred).mul(100),
          ),
        },
        {
          key: "fifty",
          denomination: 50,
          safeDropItem: safeDropItem.fifty,
          safeDrop: safeDropEndShiftItem.fifty || 0,
          total: Number(Big(safeDropItem.fifty).add(safeDropEndShiftItem.fifty)),
          totalAmount: Number(Big(safeDropItem.fifty).add(safeDropEndShiftItem.fifty).mul(50)),
        },
        {
          key: "twenty",
          denomination: 20,
          safeDropItem: safeDropItem.twenty,
          safeDrop: safeDropEndShiftItem.twenty || 0,
          total: Number(Big(safeDropItem.twenty).add(safeDropEndShiftItem.twenty)),
          totalAmount: Number(Big(safeDropItem.twenty).add(safeDropEndShiftItem.twenty).mul(20)),
        },
        {
          key: "twentyCoins",
          denomination: 20,
          safeDropItem: safeDropItem.twentyCoins,
          safeDrop: safeDropEndShiftItem.twentyCoins || 0,
          total: Number(Big(safeDropItem.twentyCoins).add(safeDropEndShiftItem.twentyCoins)),
          totalAmount: Number(
            Big(safeDropItem.twentyCoins).add(safeDropEndShiftItem.twentyCoins).mul(20),
          ),
        },
        {
          key: "ten",
          denomination: 10,
          safeDropItem: safeDropItem.ten,
          safeDrop: safeDropEndShiftItem.ten || 0,
          total: Number(Big(safeDropItem.ten).add(safeDropEndShiftItem.ten)),
          totalAmount: Number(Big(safeDropItem.ten).add(safeDropEndShiftItem.ten).mul(10)),
        },
        {
          key: "five",
          denomination: 5,
          safeDropItem: safeDropItem.five,
          safeDrop: safeDropEndShiftItem.five || 0,
          total: Number(Big(safeDropItem.five).add(safeDropEndShiftItem.five)),
          totalAmount: Number(Big(safeDropItem.five).add(safeDropEndShiftItem.five).mul(5)),
        },
        {
          key: "coinAmount",
          denomination: 1,
          safeDropItem: safeDropItem.coinAmount,
          safeDrop: safeDropEndShiftItem.coinAmount || 0,
          total: Number(Big(safeDropItem.coinAmount).add(safeDropEndShiftItem.coinAmount)),
          totalAmount: Number(
            Big(safeDropItem.coinAmount).add(safeDropEndShiftItem.coinAmount).mul(1),
          ),
        },
      ];

      if (res.shift.status === ShiftStatus.Started) {
        safeDropsList = safeDropsList.map((row) => ({
          ...row,
          safeDrop: 0, // End of Shift
          safeDropItem: row.safeDrop + row.safeDropItem,
        }));
      }

      setSafeDrop(safeDropsList);

      //set initial value of variance
      const { expense } = res;
      const totalExpenses = sumBy(expense, "amount") || 0;
      const { beginningCashBalance } = res.shift;
      const cashTransactions = res?.transactionPaymentMethod?.cash || [];
      const cashRefunds = (res?.transactionPaymentMethod?.refund || []).filter(
        (r) => r.method === "cash",
      );
      const netCashSales = Big(
        cashTransactions.reduce((sum, t) => Big(sum).add(t.finalAmount || 0), Big(0)),
      )
        .add(cashRefunds.reduce((sum, r) => Big(sum).add(r.finalAmountPaid || 0), Big(0)))
        .round(2);
      const totalSafeDrop = sumBy(safeDropsList, "totalAmount") || 0;
      const cashOnHand = Big(beginningCashBalance || 0)
        .add(netCashSales)
        .minus(totalExpenses);
      setVariance(computeCashVariance({ totalSafeDrop, cashOnHand: Number(cashOnHand) }));
    }
  });

  const { request: getRecentShiftRequest, result: getRecentShiftResult } = useApi({
    api: getShiftByShiftId,
    modalError: true,
    params: {},
  });

  const {
    request: getShiftRequest,
    loading: loadingShift,
    // result: getShiftResultOriginal,
  } = useApi({
    api: getShiftByShiftId,
    modalError: true,
    params: {
      shiftId,
    },
  });

  // set true to test first ever shift
  const testFirstShift = false;
  // const getShiftResult = useMemo(() => {
  //   if (testFirstShift) {
  //     return {
  //       ...getShiftResultOriginal,
  //       shift: {
  //         ...getShiftResultOriginal?.shift,
  //         status: "ended",
  //       },
  //       recentShift: null,
  //     };
  //   }

  //   return getShiftResultOriginal;
  // }, [getShiftResultOriginal, testFirstShift]);

  // Refetch function to reload shift data
  const refetchShift = useCallback(async () => {
    const res = await getShiftRequest();
    setShiftResult(res);
  }, [getShiftRequest]);

  const updatePaymentMethodRefNumber = useCallback((transactionPaymentMethodId, newRefNumber) => {
    setShiftResult((prev) => {
      if (!prev) return prev;
      const applyRefNumber = (item) =>
        item.transactionPaymentMethodId === transactionPaymentMethodId
          ? {
              ...item,
              originalRefNumber:
                newRefNumber !== item.refNumber ? (item.refNumber ?? "") : item.originalRefNumber,
              refNumber: newRefNumber,
              adjustedAt:
                newRefNumber !== item.refNumber ? new Date().toISOString() : item.adjustedAt,
            }
          : item;

      return {
        ...prev,
        transactionPaymentMethod: {
          ...prev.transactionPaymentMethod,
          cash: prev.transactionPaymentMethod.cash.map(applyRefNumber),
          nonCash: prev.transactionPaymentMethod.nonCash.map(applyRefNumber),
        },
      };
    });
  }, []);

  const updateTransactionPaymentMethod = useCallback((transactionPaymentMethodId, changes) => {
    const { method: newMethod, refNumber: newRefNumber } = changes ?? {};

    setShiftResult((prev) => {
      if (!prev || !transactionPaymentMethodId) return prev;

      const matches = (item) => item.transactionPaymentMethodId === transactionPaymentMethodId;
      const applyChange = (item) => ({
        ...item,
        originalMethod: item.originalMethod ?? item.method,
        originalRefNumber:
          newRefNumber !== item.refNumber ? (item.refNumber ?? "") : item.originalRefNumber,
        method: newMethod,
        refNumber: newRefNumber,
        adjustedAt: new Date().toISOString(),
        ...(isAccountabilityPaymentMethod(newMethod)
          ? isAccountabilityPaymentMethod(item.method)
            ? {}
            : { actionNeeded: true, accountability: [] }
          : { actionNeeded: false, accountability: undefined }),
      });

      const { cash = [], nonCash = [], cashTransactions } = prev.transactionPaymentMethod;

      if (newMethod === PaymentMethod.Cash) {
        const updatedCash = cash.map((item) => (matches(item) ? applyChange(item) : item));
        const movedNonCash = nonCash.filter(matches).map(applyChange);
        return {
          ...prev,
          transactionPaymentMethod: {
            ...prev.transactionPaymentMethod,
            cash: cashTransactions
              ? addToAggregatedCash(updatedCash, movedNonCash)
              : updatedCash.concat(movedNonCash),
            nonCash: nonCash.filter((item) => !matches(item)),
            ...(cashTransactions && {
              cashTransactions: cashTransactions.concat(movedNonCash),
            }),
          },
        };
      }

      const movedCashRows = cash.filter(matches);
      const movedCashTransactions = (cashTransactions ?? []).filter(matches);
      const cashAfterMove = movedCashRows.length
        ? cash.filter((item) => !matches(item))
        : deductFromAggregatedCash(cash, movedCashTransactions);

      return {
        ...prev,
        transactionPaymentMethod: {
          ...prev.transactionPaymentMethod,
          cash: cashAfterMove,
          nonCash: [...nonCash.map((item) => (matches(item) ? applyChange(item) : item))].concat(
            (movedCashTransactions.length ? movedCashTransactions : movedCashRows).map(applyChange),
          ),
          ...(cashTransactions && {
            cashTransactions: cashTransactions.filter((item) => !matches(item)),
          }),
        },
      };
    });
  }, []);

  const updateTransactionAccountability = useCallback(
    (transactionPaymentMethodId, accountability) => {
      setShiftResult((prev) => {
        if (!prev || !transactionPaymentMethodId) return prev;

        return {
          ...prev,
          transactionPaymentMethod: {
            ...prev.transactionPaymentMethod,
            nonCash: prev.transactionPaymentMethod.nonCash.map((item) =>
              item.transactionPaymentMethodId === transactionPaymentMethodId
                ? { ...item, accountability, actionNeeded: !accountability.length }
                : item,
            ),
          },
        };
      });
    },
    [],
  );

  const updateShift = useApi({
    api: updateShiftById,
    handleOwnError: true,
    params: {
      shiftId,
    },
  });

  const { request: validationRequest } = useApi({
    api: validateShiftByShiftId,
    handleOwnError: true,
    params: {
      shiftId,
    },
  });

  const fetchValidation = async () => {
    try {
      await validationRequest();
    } catch (err) {
      if (err.status === 400) {
        setExistingShiftOpen(true);
      }
    }
  };

  const setFieldCode = (field) => {
    let productCode;
    if (field.includes("Diesel")) {
      productCode = ProductCode.DIESEL;
    }

    if (field.includes("Gas91")) {
      productCode = ProductCode.GAS91;
    }

    if (field.includes("Gas95")) {
      productCode = ProductCode.GAS95;
    }

    if (field.includes("Gas97")) {
      productCode = ProductCode.GAS97;
    }

    return productCode;
  };

  // const transformData = useMemo(() => {
  //   const getShiftDetailFields = [
  //     "shiftNumber",
  //     "isReadingDipstickBeginning",
  //     "isReadingDipstickEnding",
  //   ];

  //   const getDipstickFields = [
  //     "beginningDiesel",
  //     "beginningGas91",
  //     "beginningGas95",
  //     "beginningGas97",
  //     "beginningDieselCm",
  //     "beginningGas91Cm",
  //     "beginningGas95Cm",
  //     "beginningGas97Cm",
  //     "endingDiesel",
  //     "endingGas91",
  //     "endingGas95",
  //     "endingGas97",
  //     "endingDieselCm",
  //     "endingGas91Cm",
  //     "endingGas95Cm",
  //     "endingGas97Cm",
  //   ];

  //   let state = {};

  //   if (getShiftResult) {
  //     const isReadingDipstickBeginning = getShiftResult?.shift.isReadingDipstickBeginning;
  //     const isReadingDipstickEnding = getShiftResult?.shift.isReadingDipstickEnding;

  //     const shiftDetailsResult = getShiftDetailFields.map((field) => ({
  //       [field]:
  //         field === "shiftNumber" ? getShiftResult?.shift[field] : !getShiftResult?.shift[field],
  //     }));

  //     const stationProducts = getShiftResult?.station?.stationUgt.map((item) => item.productCode);

  //     shiftDetailsResult.forEach((item) => {
  //       const key = Object.keys(item)[0];

  //       state[key] = {
  //         value: item[key],
  //         disabled: false,
  //         required: key === "shiftNumber" && pageMode === FormMode.Edit,
  //       };
  //     });

  //     const dipstickResult = getDipstickFields.map((field) => ({
  //       [field]: parseFloat(getShiftResult?.shift[field]).toFixed(3),
  //     }));

  //     const stationUgts = getShiftResult?.station?.stationUgt;

  //     const getMaxLiter = (fieldName) => {
  //       fieldName = fieldName?.toLowerCase();
  //       if (fieldName.includes("cm")) return undefined;

  //       const product = stationUgts?.find((ugt) => fieldName?.includes(ugt?.productCode));
  //       if (!product) return undefined;

  //       return product?.liter;
  //     };

  //     dipstickResult.forEach((item) => {
  //       const key = Object.keys(item)[0];
  //       if (
  //         [
  //           "beginningDiesel",
  //           "beginningGas91",
  //           "beginningGas95",
  //           "beginningGas97",
  //           "beginningDieselCm",
  //           "beginningGas91Cm",
  //           "beginningGas95Cm",
  //           "beginningGas97Cm",
  //         ].includes(key)
  //       ) {
  //         state[key] = {
  //           value: isReadingDipstickBeginning ? item[key] : "",
  //           disabled: !isReadingDipstickBeginning,
  //           required: !isReadingDipstickBeginning && stationProducts.includes(setFieldCode(key)),
  //           dirty: !isReadingDipstickBeginning,
  //         };
  //       } else if (
  //         [
  //           "endingDiesel",
  //           "endingGas91",
  //           "endingGas95",
  //           "endingGas97",
  //           "endingDieselCm",
  //           "endingGas91Cm",
  //           "endingGas95Cm",
  //           "endingGas97Cm",
  //         ].includes(key)
  //       ) {
  //         state[key] = {
  //           value: isReadingDipstickEnding ? item[key] : "",
  //           disabled: !isReadingDipstickEnding,
  //           required: !isReadingDipstickEnding && stationProducts.includes(setFieldCode(key)),
  //           dirty: !isReadingDipstickEnding,
  //         };
  //       } else {
  //         state[key] = {
  //           value: item[key],
  //           disabled: false,
  //           required: stationProducts.includes(setFieldCode(key)),
  //         };
  //       }

  //       state[key] = {
  //         ...state[key],
  //         maxLiter: getMaxLiter(key),
  //       };
  //     });

  //     const shiftEmployees = getShiftResult?.shift.employees;

  //     if (shiftEmployees) {
  //       const newEmployees = shiftEmployees.map((i) => {
  //         i.nameCheck = false;
  //         i.agencyCheck = false;
  //         i.labelCheck = false;
  //         return i;
  //       });

  //       setEmployees(sortBy(newEmployees, "shiftEmployeeId"));
  //     }

  //     const initialInventory = getShiftResult?.shift.inventories.map((item) => ({
  //       shiftInventoryId: item.shiftInventoryId,
  //       beginningInventory: item.beginningInventory,
  //       endingInventory: item.endingInventory,
  //     }));

  //     setInventory(initialInventory);

  //     const requiredFieldsBeginning = validateReadingDipstick(
  //       "isReadingDipstickBeginning",
  //       false,
  //       state,
  //       false,
  //       getShiftResult.recentShift
  //     );

  //     const requiredFieldsEnding = validateReadingDipstick(
  //       "isReadingDipstickEnding",
  //       false,
  //       state,
  //       false,
  //       getShiftResult.recentShift
  //     );
  //     state = {
  //       ...state,
  //       ...requiredFieldsBeginning,
  //       ...requiredFieldsEnding,
  //     };
  //   }
  //   return state;
  // }, [getShiftResult, pageMode]);

  const setFormData = (shiftResult) => {
    // test as first ever shift
    if (testFirstShift) {
      shiftResult = {
        ...shiftResult,
        shift: {
          ...shiftResult?.shift,
          status: "ended",
        },
        recentShift: null,
      };
    }
    const getShiftDetailFields = [
      "shiftNumber",
      "isReadingDipstickBeginning",
      "isReadingDipstickEnding",
    ];

    const getDipstickFields = [
      "beginningDiesel",
      "beginningGas91",
      "beginningGas95",
      "beginningGas97",
      "beginningDieselCm",
      "beginningGas91Cm",
      "beginningGas95Cm",
      "beginningGas97Cm",
      "endingDiesel",
      "endingGas91",
      "endingGas95",
      "endingGas97",
      "endingDieselCm",
      "endingGas91Cm",
      "endingGas95Cm",
      "endingGas97Cm",
    ];

    let state = {};

    if (shiftResult) {
      const isReadingDipstickBeginning = shiftResult?.shift.isReadingDipstickBeginning;
      const isReadingDipstickEnding = shiftResult?.shift.isReadingDipstickEnding;

      const shiftDetailsResult = getShiftDetailFields.map((field) => ({
        [field]: field === "shiftNumber" ? shiftResult?.shift[field] : !shiftResult?.shift[field],
      }));

      const stationProducts = shiftResult?.station?.stationUgt.map((item) => item.productCode);

      shiftDetailsResult.forEach((item) => {
        const key = Object.keys(item)[0];

        state[key] = {
          value: item[key],
          disabled: false,
          required: key === "shiftNumber" && pageMode === FormMode.Edit,
        };
      });

      state.shiftDate = {
        value:
          shiftResult?.shift?.shiftDate || shiftResult?.shift?.startShift
            ? new Date(shiftResult.shift.shiftDate || shiftResult.shift.startShift)
            : null,
        disabled: false,
        required: pageMode === FormMode.Edit,
      };

      const dipstickResult = getDipstickFields.map((field) => ({
        [field]: formatNumber(shiftResult?.shift[field], field.includes("Cm") ? 4 : 3),
      }));

      const stationUgts = shiftResult?.station?.stationUgt;

      const getMaxLiter = (fieldName) => {
        fieldName = fieldName?.toLowerCase();
        if (fieldName.includes("cm")) return undefined;

        const product = stationUgts?.find((ugt) => fieldName?.includes(ugt?.productCode));
        if (!product) return undefined;

        return product?.liter;
      };
      dipstickResult.forEach((item) => {
        const key = Object.keys(item)[0];
        if (
          [
            "beginningDiesel",
            "beginningGas91",
            "beginningGas95",
            "beginningGas97",
            "beginningDieselCm",
            "beginningGas91Cm",
            "beginningGas95Cm",
            "beginningGas97Cm",
          ].includes(key)
        ) {
          state[key] = {
            value: isReadingDipstickBeginning ? item[key] : "",
            disabled: !isReadingDipstickBeginning,
            required: !isReadingDipstickBeginning && stationProducts.includes(setFieldCode(key)),
            dirty: !isReadingDipstickBeginning,
          };
        } else if (
          [
            "endingDiesel",
            "endingGas91",
            "endingGas95",
            "endingGas97",
            "endingDieselCm",
            "endingGas91Cm",
            "endingGas95Cm",
            "endingGas97Cm",
          ].includes(key)
        ) {
          state[key] = {
            value: isReadingDipstickEnding ? item[key] : "",
            disabled: !isReadingDipstickEnding,
            required: !isReadingDipstickEnding && stationProducts.includes(setFieldCode(key)),
            dirty: !isReadingDipstickEnding,
          };
        } else {
          state[key] = {
            value: item[key],
            disabled: false,
            required: stationProducts.includes(setFieldCode(key)),
          };
        }

        state[key] = {
          ...state[key],
          maxLiter: getMaxLiter(key),
        };
      });

      const shiftEmployees = shiftResult?.shift.employees;

      if (shiftEmployees) {
        const newEmployees = shiftEmployees.map((i) => {
          const isCashier = i.label === "Cashier";
          const resolvedTimeIn = i.timeInAt || (isCashier ? shiftResult.shift.startShift : null);
          const resolvedTimeOut = i.timeOutAt || (isCashier ? shiftResult.shift.endShift : null);
          return {
            ...i,
            nameCheck: false,
            agencyCheck: false,
            labelCheck: false,
            timeInAt: resolvedTimeIn,
            timeOutAt: resolvedTimeOut,
            isStraightDuty: i.isStraightDuty || false,
            timeInAtCheck: !resolvedTimeIn,
            timeOutAtCheck: !resolvedTimeOut,
          };
        });

        if (pageMode === FormMode.Edit) {
          const alreadyHasVariance = newEmployees.some(
            (e) => e.name?.toLowerCase() === "stocq variance",
          );
          if (!alreadyHasVariance) {
            newEmployees.push({
              ...newEmployees[0],
              name: "Stocq Variance",
              label: "Stocq Variance",
              shiftEmployeeId: newEmployees[newEmployees.length - 1].shiftEmployeeId + 1,
              shoulderAmount: 0,
            });
          }
        }

        setEmployees(sortBy([...newEmployees], "shiftEmployeeId"));
      }

      setBeginningBalance(shiftResult?.shift.beginningCashBalance);

      // const initialInventory = shiftResult?.shift.inventories.map((item) => {
      //   return {
      //     shiftInventoryId: item.shiftInventoryId,
      //     beginningInventory: item.beginningInventory,
      //     endingInventory: item.endingInventory,
      //   };
      // });

      // setInventory(initialInventory);

      const requiredFieldsBeginning = validateReadingDipstick(
        "isReadingDipstickBeginning",
        false,
        state,
        false,
        shiftResult.recentShift,
      );

      const requiredFieldsEnding = validateReadingDipstick(
        "isReadingDipstickEnding",
        false,
        state,
        false,
        shiftResult.recentShift,
      );
      state = {
        ...state,
        ...requiredFieldsBeginning,
        ...requiredFieldsEnding,
      };
    }
    return state;
  };

  const form = useMemo(() => {
    // let initialState = transformData || {};
    // return formState(initialState);
    return formState();
  }, []);

  const formData = useForm({
    initialState: form,
  });

  const { getFormValues } = formData;

  const shift = getShiftResult?.shift;

  const totalTransactionCount = shift?.totalTransactionCount || 0;
  const totalBosTransactionCount = shift?.totalBosTransactionCount || 0;
  const progress = calculateSyncProgress({
    totalBosTransactionCount: totalBosTransactionCount,
    totalTransactionCount,
  });

  const onEditDetails = () => {
    history.push(Path.ViewShiftDetailsInfoById(shiftId, FormMode.Edit));
  };

  const pumpDiscounts = useMemo(() => getShiftResult?.shiftDiscounts || [], [getShiftResult]);

  const submitForm = useCallback(
    async (remarksForceClose) => {
      try {
        const getFields = [
          "beginningDiesel",
          "beginningGas91",
          "beginningGas95",
          "beginningGas97",
          "beginningDieselCm",
          "beginningGas91Cm",
          "beginningGas95Cm",
          "beginningGas97Cm",
          "endingDiesel",
          "endingGas91",
          "endingGas95",
          "endingGas97",
          "endingDieselCm",
          "endingGas91Cm",
          "endingGas95Cm",
          "endingGas97Cm",
        ];

        const params = getFormValues();

        getFields.forEach((field) => {
          if (params[field]) {
            params[field] = parseFloat(params[field]?.replace(/,/g, ""));
          } else {
            delete params[field];
          }
        });

        if (params.shiftDate) {
          params.shiftDate = moment(params.shiftDate).format("YYYY-MM-DD");
        }

        if (employees && employees.length > 0) {
          params.employees = employees;

          if (employeesToPay && employeesToPay.length) {
            params.employees = params.employees.map((i, idx) => {
              const result = employeesToPay.find((ep) => ep.value === employeeShoulderKey(i, idx));
              let shoulderAmount = 0;

              if (result && result.shoulderAmount) {
                shoulderAmount = stringToDecimal(String(result.shoulderAmount));
              }

              return {
                ...i,
                shoulderAmount,
              };
            });
          }

          params.employees = params.employees
            .filter((e) =>
              e.name?.toLowerCase() === "stocq variance" && Number(e.shoulderAmount) === 0
                ? false
                : true,
            )
            .map(
              ({ nameCheck, agencyCheck, labelCheck, timeInAtCheck, timeOutAtCheck, ...emp }) => ({
                ...emp,
                timeInAt: emp.timeInAt ? moment(emp.timeInAt).format("YYYY-MM-DD HH:mm:ss") : null,
                timeOutAt: emp.timeOutAt
                  ? moment(emp.timeOutAt).format("YYYY-MM-DD HH:mm:ss")
                  : null,
              }),
            );
        }

        params.inventories = inventory.map((item) => {
          const beginning =
            typeof item.beginningInventory === "string"
              ? parseFloat(item.beginningInventory)
              : item.beginningInventory;

          const ending =
            typeof item.endingInventory === "string"
              ? parseFloat(item.endingInventory)
              : item.endingInventory;

          // blank (null) inventories are sent back as null instead of 0
          return {
            ...item,
            beginningInventory: isNaN(beginning) || beginning === null ? null : Number(beginning),
            endingInventory: isNaN(ending) || ending === null ? null : Number(ending),
          };
        });

        if (safeDrop) {
          const newSafeDrop = {};
          let totalAmount = 0;
          safeDrop.forEach((i) => {
            totalAmount = Number(
              Big(totalAmount).add(Number(Big(i.denomination).mul(Number(i.safeDrop)))),
            );
            newSafeDrop[i.key] = Number(i.safeDrop);
          });

          newSafeDrop.safeDropId = safeDropId;

          newSafeDrop.amount = totalAmount;

          params.safeDrop = newSafeDrop;

          params.actualCash = sumBy(safeDrop, "totalAmount") || 0;

          if (variance > 0) {
            params.excess = variance;
            params.shortage = 0;
          } else if (variance < 0) {
            params.excess = 0;
            params.shortage = Math.abs(variance);
          } else {
            params.excess = 0;
            params.shortage = 0;
          }
        }

        params.isReadingDipstickBeginning = !beginDidNotPerformDipStick;
        params.isReadingDipstickEnding = !endDidNotPerformDipStick;

        const fuelTransactions = [
          ...getProductPumpsWithTransactions({
            productCode: ProductCode.DIESEL,
            currentShift: getShiftResult,
          }),
          ...getProductPumpsWithTransactions({
            productCode: ProductCode.GAS91,
            currentShift: getShiftResult,
          }),
          ...getProductPumpsWithTransactions({
            productCode: ProductCode.GAS95,
            currentShift: getShiftResult,
          }),
          ...getProductPumpsWithTransactions({
            productCode: ProductCode.GAS97,
            currentShift: getShiftResult,
          }),
        ];
        params.manualTransactions = [];
        params.shiftPumpTotals.forEach((pumpTotalizer) => {
          const pumpNumber = Number(String(pumpTotalizer.fuelingPointId).split(" ")[1]);

          const productNames = new Set();

          Object.keys(pumpTotalizer).forEach((key) => {
            if (key.startsWith("startTotalizer") && !key.toLowerCase().includes("old")) {
              productNames.add(key.replace("startTotalizer", ""));
            }
          });

          const manualTransactions = [];
          Array.from(productNames).forEach((productName) => {
            const productCode = productName.toLowerCase();
            const startKey = `startTotalizer${productName}`;
            const endKey = `endTotalizer${productName}`;

            const startValue = Number(removeComasStringAmount(pumpTotalizer[startKey] ?? 0));
            const endValue = Number(removeComasStringAmount(pumpTotalizer[endKey] ?? 0));

            const saleTransaction = fuelTransactions.find(
              (transaction) =>
                transaction.pump === pumpTotalizer.fuelingPointId &&
                transaction.productCode === productCode,
            );

            const pumpPricePerLiter =
              find(getShiftResult.shift.pricing, { product: productCode })?.pricePerLiter || 0;
            const pumpDiscount = pumpDiscounts.find(
              (discount) =>
                discount.product === productCode && Number(discount.pumpSlot) === pumpNumber,
            );

            const quantity = Number(
              Big(endValue)
                .minus(startValue)
                .minus(saleTransaction ? String(saleTransaction.quantity) : "0")
                .toFixed(4),
            );

            const absQuantity = Big(quantity).abs();
            if (absQuantity.lt(0.1)) {
              return;
            }

            const discount = pumpDiscount?.discountPerLiter || 0;

            const totalDiscount = Big(quantity).times(discount).toNumber();

            const totalAmount = Big(quantity)
              .times(pumpPricePerLiter)
              .minus(totalDiscount)
              .toNumber();

            manualTransactions.push({
              pumpPrice: pumpPricePerLiter,
              quantity,
              totalAmount,
              totalDiscount,
              discount: discount,
              productCode: productCode,
              fuelingPoint: pumpTotalizer.fuelingPointId,
              productName: prettifyProductCode(productCode),
              sku: saleTransaction?.sku || productCode,
              metadata:
                saleTransaction?.metadata?.replace(/^"|"$/g, "") ?? pumpTotalizer.fuelingPointId,
            });
          });

          params.manualTransactions = [...params.manualTransactions, ...manualTransactions];
        });

        params.shiftPumpTotals = params.shiftPumpTotals.map((shiftPumpTotal) => {
          const copy = { ...shiftPumpTotal };
          Object.keys(copy).forEach((key) => {
            copy[key] = key.toLowerCase().includes("totalizer")
              ? removeComasStringAmount(copy[key])
              : copy[key];

            if (
              !(key.toLowerCase().includes("total") || key.toLowerCase().includes("remarks")) ||
              key.toLowerCase().includes("old")
            ) {
              delete copy[key];
            }
          });
          return copy;
        });

        if (remarksUgt) {
          params.remarksUgt = remarksUgt;
        }

        if (remarksForceClose) {
          params.remarksForceClose = remarksForceClose;
        }

        console.log("submitForm", params);
        await updateShift.request({
          ...params,
          status: ShiftStatus.Closed,
        });

        forceCloseModal.close();
        successModal.show();
      } catch (e) {
        console.log("submitForm", e);
        e.showError();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getFormValues,
      remarksUgt,
      employees,
      safeDrop,
      variance,
      beginDidNotPerformDipStick,
      endDidNotPerformDipStick,
      successModal,
      forceCloseModal,
      employeesToPay,
      safeDropId,
      updateShift,
      inventory,
      getShiftResult,
    ],
  );

  const isShiftEndedCompletely =
    getShiftResult &&
    [ShiftStatus.Ended, ShiftStatus.Closed].includes(getShiftResult?.shift.status) &&
    progress >= 100;

  const showEditButton = useCallback(() => {
    return getShiftResult && getShiftResult?.shift.status === ShiftStatus.Ended;
  }, [getShiftResult]);

  const showCloseShiftButton = () => {
    // return getShiftResult && getShiftResult?.shift.status === ShiftStatus.Ended && progress >= 100;
    return getShiftResult && getShiftResult?.shift.status === ShiftStatus.Ended;
  };

  const totalShoulderAmount = useMemo(() => {
    let totalShoulderAmount = 0;
    employeesToPay.forEach((i) => {
      totalShoulderAmount = Number(
        Big(totalShoulderAmount).add(parseNumber(i.shoulderAmount) || 0),
      );
    });

    return totalShoulderAmount;
  }, [employeesToPay]);

  const seededEmployeesToPay = useRef(false);

  useEffect(() => {
    if (seededEmployeesToPay.current || employees.length === 0) {
      return;
    }
    seededEmployeesToPay.current = true;

    if (employeesToPay.length > 0) {
      return;
    }

    const newEmployeesToPay = [];
    employees.forEach((i, idx) => {
      if (Big(i.shoulderAmount || 0).gt(0)) {
        newEmployeesToPay.push({
          label: i.name,
          value: employeeShoulderKey(i, idx),
          shoulderAmount: i.shoulderAmount,
          nameCheck: false,
          amountCheck: false,
        });
      }
    });

    if (newEmployeesToPay.length > 0) {
      setEmployeesToPay(newEmployeesToPay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  const defaultRowAdded = useRef(false);

  useEffect(() => {
    if (
      !defaultRowAdded.current &&
      pageMode === FormMode.Edit &&
      Big(variance).lt(0) &&
      employeesToPay.length === 0
    ) {
      defaultRowAdded.current = true;
      const isReopen = !!getShiftResult?.shift?.closedAt;
      if (!isReopen) {
        setEmployeesToPay([
          {
            label: "",
            value: "",
            shoulderAmount: 0,
            nameCheck: false,
            amountCheck: false,
          },
        ]);
      }
    }
  }, [
    variance,
    employeesToPay.length,
    pageMode,
    setEmployeesToPay,
    getShiftResult?.shift?.closedAt,
    employees,
  ]);

  const incompleteByTab = useMemo(
    () =>
      getIncompleteFieldsByTab({
        pageMode,
        fields: formData.fields,
        employees,
        employeesToPay,
        safeDrop,
        variance,
        totalShoulderAmount,
        recentShift: getShiftResult?.recentShift,
        role: auth.accessRole.role,
      }),
    [
      pageMode,
      formData.fields,
      employees,
      employeesToPay,
      safeDrop,
      variance,
      totalShoulderAmount,
      getShiftResult?.recentShift,
      auth.accessRole.role,
    ],
  );

  const totalIncompleteFields = useMemo(
    () => countIncompleteFields(incompleteByTab),
    [incompleteByTab],
  );

  const isIncompleteCountReady = !isLoading && Boolean(getShiftResult);

  const activeTabIncompleteFields = useMemo(
    () => incompleteByTab[page] || [],
    [incompleteByTab, page],
  );

  const goToTab = useCallback((nextPage) => {
    setHighlighted(false);
    highlightCursor.current = 0;
    setPage(nextPage);
  }, []);

  const items = useMemo(() => {
    return [
      {
        text: locale.shiftDetails,
        page: ShiftDetailsTab.Info,
        badge:
          SHOW_INCOMPLETE_FIELD_IDENTIFIER && isIncompleteCountReady
            ? incompleteByTab[ShiftDetailsTab.Info].length
            : 0,
        onClick: () => {
          goToTab(ShiftDetailsTab.Info);
        },
      },
      {
        text: locale.wetStockInventory,
        page: ShiftDetailsTab.WetStock,
        badge:
          SHOW_INCOMPLETE_FIELD_IDENTIFIER && isIncompleteCountReady
            ? incompleteByTab[ShiftDetailsTab.WetStock].length
            : 0,
        loading: isLoading,
        onClick: () => {
          goToTab(ShiftDetailsTab.WetStock);
        },
      },
      {
        text: locale.dryStockInventory,
        page: ShiftDetailsTab.DryStock,
        badge:
          SHOW_INCOMPLETE_FIELD_IDENTIFIER && isIncompleteCountReady
            ? incompleteByTab[ShiftDetailsTab.DryStock].length
            : 0,
        onClick: () => {
          goToTab(ShiftDetailsTab.DryStock);
        },
      },
      {
        text: locale.cashAndNonCash,
        page: ShiftDetailsTab.CashAndNonCash,
        badge:
          SHOW_INCOMPLETE_FIELD_IDENTIFIER && isIncompleteCountReady
            ? incompleteByTab[ShiftDetailsTab.CashAndNonCash].length
            : 0,
        onClick: () => {
          goToTab(ShiftDetailsTab.CashAndNonCash);
        },
      },
    ];
  }, [isLoading, isIncompleteCountReady, incompleteByTab, goToTab]);

  const scrollToIncompleteField = useCallback(
    (index) => {
      const anchors = [
        ...new Set(
          activeTabIncompleteFields
            .map((entry) => entry.anchorId && document.getElementById(entry.anchorId))
            .filter(Boolean),
        ),
      ];

      if (anchors.length === 0) {
        return;
      }

      anchors[index % anchors.length].scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [activeTabIncompleteFields],
  );

  const onHighlightIncompleteFields = useCallback(() => {
    formData.validateForm();
    setHighlighted(true);
    highlightCursor.current = 0;
    scrollToIncompleteField(0);
  }, [formData, scrollToIncompleteField]);

  const onGoToNextIncompleteField = useCallback(() => {
    highlightCursor.current += 1;
    scrollToIncompleteField(highlightCursor.current);
  }, [scrollToIncompleteField]);

  const isShowEditShift =
    pageMode === FormMode.Edit && showCloseShiftButton()
      ? locale.closeShift
      : pageMode === FormMode.View && showEditButton()
        ? locale.editDetails
        : null;

  const isActionDisabled = () => {
    if (pageMode === FormMode.View) {
      return false;
    }
    if (loadingProducts || allProducts.length <= 0) {
      return true;
    }
    return totalIncompleteFields > 0;
  };

  // init inventory
  useEffect(() => {
    if (allProducts?.length > 0 && getShiftResult) {
      const dryStockDeliveryIds = new Set(
        getShiftResult.delivery.deliveries
          ?.filter((d) => d.productType !== "fuel")
          ?.map((d) => d.deliveryId) || [],
      );
      const computedQuantity = getShiftResult.delivery.deliveryProducts
        .filter((dp) => dryStockDeliveryIds.has(dp.deliveryId))
        .reduce((acc, dp) => {
          const sku = dp.product;
          const quantity = dp.quantityDelivered || 0;
          acc[sku] = (acc[sku] || 0) + quantity;
          return acc;
        }, {});
      const result = getShiftResult?.shift?.inventories.map((itemInventory) => {
        const product = allProducts.find(
          (p) => p.id === itemInventory.productId || p.sku === itemInventory.sku,
        );
        const sku = product?.sku;
        const totalQuantityDelivered = sku ? computedQuantity[sku] || 0 : 0;
        const isInventoryUnknown = itemInventory.beginningInventory == null;
        const beginningInventory = itemInventory.beginningInventory ?? null;
        const expectedEndingInventory = computeExpectedInventory(
          beginningInventory ?? 0,
          totalQuantityDelivered,
          itemInventory.salesInventory,
          itemInventory.returnInventory,
        );

        return {
          shiftInventoryId: itemInventory.shiftInventoryId,
          beginningInventory,
          endingInventory:
            getShiftResult?.shift?.status === ShiftStatus.Closed || isInventoryUnknown
              ? itemInventory.endingInventory
              : itemInventory.endingInventory || expectedEndingInventory,
        };
      });

      setInventory(result);
    }
  }, [allProducts, getShiftResult]);

  useEffect(() => {
    if (
      pageMode === FormMode.Edit &&
      getShiftResult &&
      // (hasExistingShiftOpen === true || progress < 100)
      hasExistingShiftOpen === true
    ) {
      history.push(Path.ViewShiftDetailsInfoById(shiftId, FormMode.View));
    }
  }, [showEditButton, pageMode, getShiftResult, history, shiftId, hasExistingShiftOpen, progress]);

  return (
    <>
      <Loader open={loadingShift} />
      <SuccessModal
        image={StocqClockIn}
        title={locale.shiftSyncRequired}
        content={locale.onlyEndedShifts}
        {...syncRequiredModal}
        close={() => {
          syncRequiredModal.close();
        }}
        primary={{
          text: locale.gotIt,
          onClick: () => {
            syncRequiredModal.close();
          },
        }}
      />
      <SuccessModal
        image={StocqClockIn}
        title={locale.prevShiftNotClosed}
        content={locale.thisShiftCannotBeEdited}
        {...prevShiftModal}
        close={() => {
          prevShiftModal.close();
        }}
        primary={{
          text: locale.gotIt,
          onClick: () => {
            prevShiftModal.close();
          },
        }}
      />
      <SuccessModal
        image={StocqClockIn}
        title={locale.closeShift}
        content={locale.successMessageModalCloseShift}
        {...successModal}
        close={() => {
          successModal.close();
          history.push(Path.ShiftRecords);
        }}
        primary={{
          text: locale.gotIt,
          onClick: () => {
            successModal.close();
            history.push(Path.ShiftRecords);
          },
        }}
      />
      <ForceCloseShiftModal
        {...forceCloseModal}
        readOnly={getShiftResult?.shift?.status === ShiftStatus.Closed}
        remarks={getShiftResult?.shift?.remarksForceClose}
        loading={updateShift?.loading}
        onSubmit={(remarks) => submitForm(remarks)}
      />
      {getShiftResult && !loadingShift && (
        <div>
          <BackLink text={locale.shiftRecords} path={Path.ShiftRecords} />
          <Intro
            title={locale.shiftReportDetails}
            actionText={isShowEditShift}
            actionDisabled={isActionDisabled()}
            actionLoading={updateShift?.loading}
            actionOnClick={() => {
              if (hasExistingShiftOpen === true) {
                return prevShiftModal.show();
              }

              if (pageMode === FormMode.View) {
                if (showEditButton()) {
                  return onEditDetails();
                }
                return;
              }

              if (progress < 100) {
                return forceCloseModal.show();
              }
              return submitForm();
            }}
          />
          {SHOW_INCOMPLETE_FIELD_IDENTIFIER &&
            isIncompleteCountReady &&
            pageMode === FormMode.Edit &&
            showCloseShiftButton() && (
              <ShiftRecordsDetailsIncompleteBanner count={totalIncompleteFields} />
            )}
          <div style={{ marginBottom: "30px", marginTop: "30px" }}>
            <PanelButtons items={items} isLink={false} page={page} />
          </div>
          {SHOW_INCOMPLETE_FIELD_IDENTIFIER &&
            isIncompleteCountReady &&
            pageMode === FormMode.Edit &&
            showCloseShiftButton() && (
              <ShiftRecordsDetailsIncompleteCounter
                count={activeTabIncompleteFields.length}
                isHighlighted={isHighlighted}
                onHighlight={onHighlightIncompleteFields}
                onNextField={onGoToNextIncompleteField}
              />
            )}
          <div>
            {page === "info" && (
              <ShiftRecordsDetailsInfoModule
                station={station}
                shift={getShiftResult}
                pageMode={pageMode}
                employees={employees}
                setEmployees={setEmployees}
                form={formData}
              />
            )}
            {page === "cash-noncash" && (
              <ShiftRecordsDetailsCashAndNonCashModule
                shift={getShiftResult}
                pageMode={pageMode}
                setSafeDrop={setSafeDrop}
                safeDrop={safeDrop}
                employees={employees}
                setEmployees={setEmployees}
                employeesToPay={employeesToPay}
                setEmployeesToPay={setEmployeesToPay}
                variance={variance}
                setVariance={setVariance}
                refetchShift={refetchShift}
                beginningBalance={beginningBalance}
                setBeginningBalance={setBeginningBalance}
                updatePaymentMethodRefNumber={updatePaymentMethodRefNumber}
                updateTransactionPaymentMethod={updateTransactionPaymentMethod}
                updateTransactionAccountability={updateTransactionAccountability}
              />
            )}
            {page === "wet-stock-inventory" && (
              <ShiftRecordsDetailsStockInventoryWetModule
                isShiftEndedCompletely={isShiftEndedCompletely}
                shift={{
                  ...getShiftResult,
                  recentShiftDetails: getRecentShiftResult,
                }}
                progress={progress}
                pageMode={pageMode}
                form={formData}
                beginDidNotPerformDipStick={beginDidNotPerformDipStick}
                setBeginDidNotPerformDipStick={setBeginDidNotPerformDipStick}
                endDidNotPerformDipStick={endDidNotPerformDipStick}
                setEndDidNotPerformDipStick={setEndDidNotPerformDipStick}
                remarksUgt={remarksUgt}
                setRemarksUgt={setRemarksUgt}
                // recentShiftDetails={getRecentShiftResult}
              />
            )}
            {page === "dry-stock-inventory" && (
              <ShiftRecordsDetailsStockInventoryDryModule
                isShiftEndedCompletely={isShiftEndedCompletely}
                shift={getShiftResult}
                pageMode={pageMode}
                form={formData}
                inventory={inventory}
                setInventory={setInventory}
                loadingProducts={loadingProducts}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ShiftRecordsDetailsModule;
